/**
 * MilestoneEventSystem (Phase OO + AGE-1478) — client-side rendering for the
 * seven star milestone mini-events defined in `star-milestones-and-sunset.md`:
 *
 *   1. **Ember Drift** (§1.2) — warm-colored particle motes drifting upward
 *      toward the dimming star for a configurable duration.
 *   2. **The Pause** (§1.5) — camera-fixed text overlay card with a gentle
 *      fade-in, held for a configurable duration, then faded out.
 *   3. **Shared Dream** (§1.3) — handled additively inside SleepOverlayScene;
 *      this system relays the event to the overlay scene via Phaser events.
 *   4. **Root-Song** (§1.4) — green-tinted particle motes rising from the
 *      ground near trees for a configurable duration.
 *   5. **First Dimming** (§1.1) — a cool sky-tint overlay that fades in and
 *      slowly settles, marking the first visible shift in the star's light.
 *   6. **The Gathering** (§1.3) — brief ghost-white presence dots pulse across
 *      the screen with a flavour card, suggesting nearby players across realms.
 *   7. **Quiet Reckoning** (§1.6) — a personal milestone Whisper displayed as
 *      a centred, delicate text card with no backdrop.
 *   8. **The Sunset** (§1.7) — a warm orange-amber overlay that sweeps in and
 *      fades, marking the star's final light before the approach completes.
 *
 * Lifecycle: constructed in `setupRoomListeners()`, destroyed on reconnect
 * or scene shutdown. Follows the same pattern as RestorationTierRenderer
 * and HeartbeatAmbientSystem.
 *
 * @packageDocumentation
 */

import * as Phaser from 'phaser';
import type { Room } from 'colyseus.js';

import {
  MSG_MILESTONE_EMBER_DRIFT,
  MSG_MILESTONE_THE_PAUSE,
  MSG_MILESTONE_SHARED_DREAM,
  MSG_MILESTONE_ROOT_SONG,
  MSG_MILESTONE_FIRST_DIMMING,
  MSG_MILESTONE_THE_GATHERING,
  MSG_MILESTONE_QUIET_RECKONING,
  MSG_MILESTONE_THE_SUNSET,
  type MilestoneEmberDriftPayload,
  type MilestoneThePausePayload,
  type MilestoneSharedDreamPayload,
  type MilestoneRootSongPayload,
  type MilestoneFirstDimmingPayload,
  type MilestoneTheGatheringPayload,
  type MilestoneQuietReckoningPayload,
  type MilestoneTheSunsetPayload,
} from '@game/shared';

import { isWebGL } from '../rendering/rendererUtils';

// ── Constants ──────────────────��─────────────────────────────────────────

const EMBER_DOT_TEXTURE_KEY = '__milestone_ember_dot__';
const EMBER_DOT_SIZE = 12;
const EMBER_DEPTH = 99;

/** Screen-space target for embers — top-right, matching DimmingStar inset. */
const STAR_SCREEN_X_FRAC = 0.92; // fraction of camera width
const STAR_SCREEN_Y = 56;        // px from top

const PAUSE_OVERLAY_DEPTH = 11_500;
const PAUSE_FADE_IN_MS = 2_000;
const PAUSE_FADE_OUT_MS = 2_000;

const TITLE_FONT = "'Almendra', serif";
const COLOR_INK = '#eae0c8';

// Root-Song particle constants
const ROOT_SONG_DOT_TEXTURE_KEY = '__milestone_root_song_dot__';
const ROOT_SONG_DOT_SIZE = 8;
const ROOT_SONG_DEPTH = 50; // below UI, above terrain
const ROOT_SONG_FADE_OUT_MS = 3_000;

// First Dimming constants
const FIRST_DIMMING_DEPTH = 200; // above terrain, below UI
const FIRST_DIMMING_FADE_IN_MS = 4_000;
const FIRST_DIMMING_FADE_OUT_MS = 5_000;

// The Gathering constants
const GATHERING_DEPTH = 12_000; // above Pause overlay
const GATHERING_FADE_IN_MS = 1_500;
const GATHERING_FADE_OUT_MS = 2_000;
const GATHERING_DOT_TEXTURE_KEY = '__milestone_gathering_dot__';
const GATHERING_DOT_SIZE = 10;

// Quiet Reckoning constants
const QUIET_RECKONING_DEPTH = 11_600; // just above Pause, below Gathering
const QUIET_RECKONING_FADE_IN_MS = 3_000;
const QUIET_RECKONING_FADE_OUT_MS = 3_000;

// The Sunset constants
const SUNSET_DEPTH = 100; // above terrain, below UI
const SUNSET_FADE_IN_MS = 6_000;
const SUNSET_FADE_OUT_MS = 8_000;

/** Phaser event key used to relay the Shared Dream to SleepOverlayScene. */
export const EVT_SHARED_DREAM = 'milestone:sharedDream' as const;

// ── System ──────────────────────────────��────────────────────────────────

export class MilestoneEventSystem {
  private readonly scene: Phaser.Scene;
  private readonly room: Pick<Room, 'onMessage'>;

  // Ember Drift state
  private emberEmitter: Phaser.GameObjects.Particles.ParticleEmitter | null = null;
  private emberFadeOutTimer: Phaser.Time.TimerEvent | null = null;

  // The Pause state
  private pauseContainer: Phaser.GameObjects.Container | null = null;
  private pauseDismissTimer: Phaser.Time.TimerEvent | null = null;

  // Root-Song state
  private rootSongEmitter: Phaser.GameObjects.Particles.ParticleEmitter | null = null;
  private rootSongFadeOutTimer: Phaser.Time.TimerEvent | null = null;

  // First Dimming state
  private firstDimmingOverlay: Phaser.GameObjects.Rectangle | null = null;
  private firstDimmingTimer: Phaser.Time.TimerEvent | null = null;

  // The Gathering state
  private gatheringContainer: Phaser.GameObjects.Container | null = null;
  private gatheringEmitter: Phaser.GameObjects.Particles.ParticleEmitter | null = null;
  private gatheringTimer: Phaser.Time.TimerEvent | null = null;

  // Quiet Reckoning state
  private quietReckoningContainer: Phaser.GameObjects.Container | null = null;
  private quietReckoningTimer: Phaser.Time.TimerEvent | null = null;

  // The Sunset state
  private sunsetOverlay: Phaser.GameObjects.Rectangle | null = null;
  private sunsetTimer: Phaser.Time.TimerEvent | null = null;

  // Unsubscribers
  private readonly unsubs: (() => void)[] = [];

  constructor(scene: Phaser.Scene, room: Pick<Room, 'onMessage'>) {
    this.scene = scene;
    this.room = room;

    this.unsubs.push(
      this.listen(MSG_MILESTONE_EMBER_DRIFT, (p: MilestoneEmberDriftPayload) =>
        this.handleEmberDrift(p),
      ),
      this.listen(MSG_MILESTONE_THE_PAUSE, (p: MilestoneThePausePayload) =>
        this.handleThePause(p),
      ),
      this.listen(MSG_MILESTONE_SHARED_DREAM, (p: MilestoneSharedDreamPayload) =>
        this.handleSharedDream(p),
      ),
      this.listen(MSG_MILESTONE_ROOT_SONG, (p: MilestoneRootSongPayload) =>
        this.handleRootSong(p),
      ),
      this.listen(MSG_MILESTONE_FIRST_DIMMING, (p: MilestoneFirstDimmingPayload) =>
        this.handleFirstDimming(p),
      ),
      this.listen(MSG_MILESTONE_THE_GATHERING, (p: MilestoneTheGatheringPayload) =>
        this.handleTheGathering(p),
      ),
      this.listen(MSG_MILESTONE_QUIET_RECKONING, (p: MilestoneQuietReckoningPayload) =>
        this.handleQuietReckoning(p),
      ),
      this.listen(MSG_MILESTONE_THE_SUNSET, (p: MilestoneTheSunsetPayload) =>
        this.handleTheSunset(p),
      ),
    );
  }

  /** Tear down all subscriptions + display objects. Idempotent. */
  destroy(): void {
    for (const unsub of this.unsubs) unsub();
    this.unsubs.length = 0;

    this.destroyEmber();
    this.destroyPause();
    this.destroyRootSong();
    this.destroyFirstDimming();
    this.destroyGathering();
    this.destroyQuietReckoning();
    this.destroySunset();
  }

  // ── Helpers ────────────��─────────────────────────────────────────────

  private listen<T>(type: string, handler: (payload: T) => void): () => void {
    const off = this.room.onMessage(type, handler);
    return typeof off === 'function' ? off : () => {};
  }

  // ── 1. Ember Drift ────────────────────��─────────────────────────────

  private handleEmberDrift(payload: MilestoneEmberDriftPayload): void {
    if (!isWebGL(this.scene)) return;
    this.destroyEmber();

    const durationMs = (payload.durationSec ?? 30) * 1_000;
    this.ensureEmberTexture();

    const cam = this.scene.cameras.main;
    const targetX = cam.width * STAR_SCREEN_X_FRAC;

    // Compute a slight horizontal bias so embers drift toward the star.
    // Particles spawned left of center drift right, and vice versa.
    const halfW = cam.width / 2;
    const biasX = targetX > halfW ? 4 : -4;

    this.emberEmitter = this.scene.add.particles(0, 0, EMBER_DOT_TEXTURE_KEY, {
      // Spawn across bottom third of screen
      x: { min: 0, max: cam.width },
      y: { min: cam.height * 0.6, max: cam.height },
      // Drift upward with a horizontal bias toward the star
      speedY: { min: -40, max: -18 },
      speedX: { min: -2 + biasX, max: 2 + biasX },
      alpha: { start: 0.7, end: 0, ease: 'Sine.easeIn' },
      scale: { start: 0.3, end: 0.6, ease: 'Quad.easeOut' },
      tint: [0xffa040, 0xff8020, 0xffcc66],
      lifespan: 4_000,
      frequency: 120,
      quantity: 1,
      blendMode: Phaser.BlendModes.ADD,
      maxParticles: 0, // unlimited until stopped
    });
    this.emberEmitter.setScrollFactor(0);
    this.emberEmitter.setDepth(EMBER_DEPTH);
    (this.scene as any).addToUI?.(this.emberEmitter);

    // Fade-in: tween the emitter alpha from 0 → 1 over 2s
    this.emberEmitter.setAlpha(0);
    this.scene.tweens.add({
      targets: this.emberEmitter,
      alpha: 1,
      duration: 2_000,
      ease: 'Sine.easeOut',
    });

    // Schedule fade-out and cleanup
    const fadeOutAt = Math.max(0, durationMs - PAUSE_FADE_OUT_MS);
    this.emberFadeOutTimer = this.scene.time.delayedCall(fadeOutAt, () => {
      if (!this.emberEmitter) return;
      this.emberEmitter.stop(); // stop new emissions
      this.scene.tweens.add({
        targets: this.emberEmitter,
        alpha: 0,
        duration: PAUSE_FADE_OUT_MS,
        ease: 'Sine.easeIn',
        onComplete: () => this.destroyEmber(),
      });
    });
  }

  private destroyEmber(): void {
    if (this.emberFadeOutTimer) {
      this.emberFadeOutTimer.destroy();
      this.emberFadeOutTimer = null;
    }
    if (this.emberEmitter) {
      this.emberEmitter.destroy();
      this.emberEmitter = null;
    }
  }

  private ensureEmberTexture(): void {
    if (this.scene.textures.exists(EMBER_DOT_TEXTURE_KEY)) return;
    const SIZE = EMBER_DOT_SIZE;
    const HALF = SIZE / 2;
    const gfx = this.scene.make.graphics({ x: 0, y: 0 }, false);
    for (let r = HALF; r >= 1; r--) {
      const alpha = (1 - r / HALF) ** 2;
      gfx.fillStyle(0xffffff, alpha);
      gfx.fillCircle(HALF, HALF, r);
    }
    gfx.generateTexture(EMBER_DOT_TEXTURE_KEY, SIZE, SIZE);
    gfx.destroy();
  }

  // ── 2. The Pause ────────────────────────────────────────────────────

  private handleThePause(payload: MilestoneThePausePayload): void {
    this.destroyPause();

    const durationMs = (payload.durationSec ?? 60) * 1_000;
    const cam = this.scene.cameras.main;
    const cx = cam.width / 2;
    const cy = cam.height / 2;

    // Semi-transparent backdrop
    const backdrop = this.scene.add
      .rectangle(0, 0, cam.width, cam.height, 0x000000, 0.5)
      .setOrigin(0, 0);

    // Overlay text
    const displayText =
      payload.text ??
      'The world holds its breath.\nEverything is still.\nEverything is listening.';

    const textObj = this.scene.add.text(cx, cy, displayText, {
      fontFamily: TITLE_FONT,
      fontSize: '28px',
      color: COLOR_INK,
      align: 'center',
      lineSpacing: 16,
      wordWrap: { width: Math.min(600, cam.width - 80) },
    });
    textObj.setOrigin(0.5, 0.5);

    this.pauseContainer = this.scene.add.container(0, 0, [backdrop, textObj]);
    this.pauseContainer.setScrollFactor(0);
    this.pauseContainer.setDepth(PAUSE_OVERLAY_DEPTH);
    this.pauseContainer.setAlpha(0);
    (this.scene as any).addToUI?.(this.pauseContainer);

    // Gentle fade-in
    this.scene.tweens.add({
      targets: this.pauseContainer,
      alpha: 1,
      duration: PAUSE_FADE_IN_MS,
      ease: 'Sine.easeOut',
    });

    // Schedule fade-out
    const fadeOutAt = Math.max(0, durationMs - PAUSE_FADE_OUT_MS);
    this.pauseDismissTimer = this.scene.time.delayedCall(fadeOutAt, () => {
      if (!this.pauseContainer) return;
      this.scene.tweens.add({
        targets: this.pauseContainer,
        alpha: 0,
        duration: PAUSE_FADE_OUT_MS,
        ease: 'Sine.easeIn',
        onComplete: () => this.destroyPause(),
      });
    });
  }

  private destroyPause(): void {
    if (this.pauseDismissTimer) {
      this.pauseDismissTimer.destroy();
      this.pauseDismissTimer = null;
    }
    if (this.pauseContainer) {
      this.pauseContainer.destroy();
      this.pauseContainer = null;
    }
  }

  // ── 3. Shared Dream ────────────────────────���───────────────────────

  private handleSharedDream(payload: MilestoneSharedDreamPayload): void {
    // Relay to the global event bus so SleepOverlayScene can pick it up
    // even if it was launched after this system was constructed.
    this.scene.game.events.emit(EVT_SHARED_DREAM, payload);
  }

  // ── 4. Root-Song ──────────────────────────────────────────────────────

  private handleRootSong(payload: MilestoneRootSongPayload): void {
    if (!isWebGL(this.scene)) return;
    this.destroyRootSong();

    const durationMs = (payload.durationSec ?? 90) * 1_000;
    this.ensureRootSongTexture();

    const cam = this.scene.cameras.main;

    this.rootSongEmitter = this.scene.add.particles(0, 0, ROOT_SONG_DOT_TEXTURE_KEY, {
      x: { min: 0, max: cam.width },
      y: { min: cam.height * 0.4, max: cam.height },
      speedY: { min: -25, max: -12 },
      speedX: { min: -3, max: 3 },
      alpha: { start: 0.6, end: 0, ease: 'Sine.easeIn' },
      scale: { start: 0.2, end: 0.5, ease: 'Quad.easeOut' },
      tint: [0x88cc44, 0xaadd66, 0xccee88, 0xddcc77],
      lifespan: 5_000,
      frequency: 100,
      quantity: 1,
      blendMode: Phaser.BlendModes.ADD,
      maxParticles: 0,
    });
    this.rootSongEmitter.setScrollFactor(0);
    this.rootSongEmitter.setDepth(ROOT_SONG_DEPTH);
    (this.scene as any).addToUI?.(this.rootSongEmitter);

    // Fade-in over 3 seconds
    this.rootSongEmitter.setAlpha(0);
    this.scene.tweens.add({
      targets: this.rootSongEmitter,
      alpha: 1,
      duration: 3_000,
      ease: 'Sine.easeOut',
    });

    // Schedule fade-out and cleanup
    const fadeOutAt = Math.max(0, durationMs - ROOT_SONG_FADE_OUT_MS);
    this.rootSongFadeOutTimer = this.scene.time.delayedCall(fadeOutAt, () => {
      if (!this.rootSongEmitter) return;
      this.rootSongEmitter.stop();
      this.scene.tweens.add({
        targets: this.rootSongEmitter,
        alpha: 0,
        duration: ROOT_SONG_FADE_OUT_MS,
        ease: 'Sine.easeIn',
        onComplete: () => this.destroyRootSong(),
      });
    });
  }

  private destroyRootSong(): void {
    if (this.rootSongFadeOutTimer) {
      this.rootSongFadeOutTimer.destroy();
      this.rootSongFadeOutTimer = null;
    }
    if (this.rootSongEmitter) {
      this.rootSongEmitter.destroy();
      this.rootSongEmitter = null;
    }
  }

  private ensureRootSongTexture(): void {
    if (this.scene.textures.exists(ROOT_SONG_DOT_TEXTURE_KEY)) return;
    const SIZE = ROOT_SONG_DOT_SIZE;
    const HALF = SIZE / 2;
    const gfx = this.scene.make.graphics({ x: 0, y: 0 }, false);
    for (let r = HALF; r >= 1; r--) {
      const alpha = (1 - r / HALF) ** 2;
      gfx.fillStyle(0xffffff, alpha);
      gfx.fillCircle(HALF, HALF, r);
    }
    gfx.generateTexture(ROOT_SONG_DOT_TEXTURE_KEY, SIZE, SIZE);
    gfx.destroy();
  }

  // ── 5. First Dimming ──────────────────────────────────────────────────

  private handleFirstDimming(payload: MilestoneFirstDimmingPayload): void {
    this.destroyFirstDimming();

    const durationMs = (payload.durationSec ?? 30) * 1_000;
    const cam = this.scene.cameras.main;

    // Cool blue-grey tint rectangle — subtle sky-shift cue.
    this.firstDimmingOverlay = this.scene.add
      .rectangle(0, 0, cam.width, cam.height, 0x8899cc, 0.18)
      .setOrigin(0, 0)
      .setScrollFactor(0)
      .setDepth(FIRST_DIMMING_DEPTH)
      .setAlpha(0);
    (this.scene as any).addToUI?.(this.firstDimmingOverlay);

    this.scene.tweens.add({
      targets: this.firstDimmingOverlay,
      alpha: 1,
      duration: FIRST_DIMMING_FADE_IN_MS,
      ease: 'Sine.easeOut',
    });

    const fadeOutAt = Math.max(0, durationMs - FIRST_DIMMING_FADE_OUT_MS);
    this.firstDimmingTimer = this.scene.time.delayedCall(fadeOutAt, () => {
      if (!this.firstDimmingOverlay) return;
      this.scene.tweens.add({
        targets: this.firstDimmingOverlay,
        alpha: 0,
        duration: FIRST_DIMMING_FADE_OUT_MS,
        ease: 'Sine.easeIn',
        onComplete: () => this.destroyFirstDimming(),
      });
    });
  }

  private destroyFirstDimming(): void {
    if (this.firstDimmingTimer) {
      this.firstDimmingTimer.destroy();
      this.firstDimmingTimer = null;
    }
    if (this.firstDimmingOverlay) {
      this.firstDimmingOverlay.destroy();
      this.firstDimmingOverlay = null;
    }
  }

  // ── 6. The Gathering ──────────────────────────────────────────────────

  private handleTheGathering(payload: MilestoneTheGatheringPayload): void {
    if (!isWebGL(this.scene)) return;
    this.destroyGathering();

    const durationMs = (payload.durationSec ?? 20) * 1_000;
    const cam = this.scene.cameras.main;
    const cx = cam.width / 2;
    const cy = cam.height / 2;

    this.ensureGatheringTexture();

    // Ghost presence dots drifting gently across the screen.
    this.gatheringEmitter = this.scene.add.particles(0, 0, GATHERING_DOT_TEXTURE_KEY, {
      x: { min: 0, max: cam.width },
      y: { min: 0, max: cam.height },
      speedX: { min: -6, max: 6 },
      speedY: { min: -6, max: 6 },
      alpha: { start: 0.35, end: 0, ease: 'Sine.easeIn' },
      scale: { start: 0.4, end: 0.8, ease: 'Quad.easeOut' },
      tint: [0xddeeff, 0xeef4ff, 0xffffff],
      lifespan: 3_500,
      frequency: 180,
      quantity: 1,
      blendMode: Phaser.BlendModes.ADD,
      maxParticles: 0,
    });
    this.gatheringEmitter.setScrollFactor(0);
    this.gatheringEmitter.setDepth(GATHERING_DEPTH + 1);

    // Flavour text card.
    const flavour = this.scene.add.text(
      cx,
      cy - 30,
      'For a moment, you could feel them all.\nEverywhere, across every realm — here.',
      {
        fontFamily: TITLE_FONT,
        fontSize: '22px',
        color: COLOR_INK,
        align: 'center',
        lineSpacing: 14,
        wordWrap: { width: Math.min(520, cam.width - 80) },
      },
    ).setOrigin(0.5, 0.5);

    this.gatheringContainer = this.scene.add.container(0, 0, [flavour]);
    this.gatheringContainer.setScrollFactor(0);
    this.gatheringContainer.setDepth(GATHERING_DEPTH);
    this.gatheringContainer.setAlpha(0);
    (this.scene as any).addToUI?.(this.gatheringContainer);
    (this.scene as any).addToUI?.(this.gatheringEmitter);

    this.scene.tweens.add({
      targets: this.gatheringContainer,
      alpha: 1,
      duration: GATHERING_FADE_IN_MS,
      ease: 'Sine.easeOut',
    });

    const fadeOutAt = Math.max(0, durationMs - GATHERING_FADE_OUT_MS);
    this.gatheringTimer = this.scene.time.delayedCall(fadeOutAt, () => {
      if (this.gatheringEmitter) this.gatheringEmitter.stop();
      if (!this.gatheringContainer) return;
      this.scene.tweens.add({
        targets: this.gatheringContainer,
        alpha: 0,
        duration: GATHERING_FADE_OUT_MS,
        ease: 'Sine.easeIn',
        onComplete: () => this.destroyGathering(),
      });
    });
  }

  private destroyGathering(): void {
    if (this.gatheringTimer) {
      this.gatheringTimer.destroy();
      this.gatheringTimer = null;
    }
    if (this.gatheringEmitter) {
      this.gatheringEmitter.destroy();
      this.gatheringEmitter = null;
    }
    if (this.gatheringContainer) {
      this.gatheringContainer.destroy();
      this.gatheringContainer = null;
    }
  }

  private ensureGatheringTexture(): void {
    if (this.scene.textures.exists(GATHERING_DOT_TEXTURE_KEY)) return;
    const SIZE = GATHERING_DOT_SIZE;
    const HALF = SIZE / 2;
    const gfx = this.scene.make.graphics({ x: 0, y: 0 }, false);
    for (let r = HALF; r >= 1; r--) {
      const alpha = (1 - r / HALF) ** 2;
      gfx.fillStyle(0xffffff, alpha);
      gfx.fillCircle(HALF, HALF, r);
    }
    gfx.generateTexture(GATHERING_DOT_TEXTURE_KEY, SIZE, SIZE);
    gfx.destroy();
  }

  // ── 7. Quiet Reckoning ────────────────────────────────────────────────

  private handleQuietReckoning(payload: MilestoneQuietReckoningPayload): void {
    this.destroyQuietReckoning();

    const durationMs = (payload.durationSec ?? 15) * 1_000;
    const cam = this.scene.cameras.main;
    const cx = cam.width / 2;
    const cy = cam.height / 2;

    const whisperText = payload.whisper || 'The world remembers.';

    // No backdrop — just the whisper, intimate and alone on screen.
    const textObj = this.scene.add.text(cx, cy, whisperText, {
      fontFamily: TITLE_FONT,
      fontSize: '24px',
      color: COLOR_INK,
      align: 'center',
      lineSpacing: 14,
      wordWrap: { width: Math.min(500, cam.width - 80) },
    });
    textObj.setOrigin(0.5, 0.5);

    this.quietReckoningContainer = this.scene.add.container(0, 0, [textObj]);
    this.quietReckoningContainer.setScrollFactor(0);
    this.quietReckoningContainer.setDepth(QUIET_RECKONING_DEPTH);
    this.quietReckoningContainer.setAlpha(0);
    (this.scene as any).addToUI?.(this.quietReckoningContainer);

    this.scene.tweens.add({
      targets: this.quietReckoningContainer,
      alpha: 1,
      duration: QUIET_RECKONING_FADE_IN_MS,
      ease: 'Sine.easeOut',
    });

    const fadeOutAt = Math.max(0, durationMs - QUIET_RECKONING_FADE_OUT_MS);
    this.quietReckoningTimer = this.scene.time.delayedCall(fadeOutAt, () => {
      if (!this.quietReckoningContainer) return;
      this.scene.tweens.add({
        targets: this.quietReckoningContainer,
        alpha: 0,
        duration: QUIET_RECKONING_FADE_OUT_MS,
        ease: 'Sine.easeIn',
        onComplete: () => this.destroyQuietReckoning(),
      });
    });
  }

  private destroyQuietReckoning(): void {
    if (this.quietReckoningTimer) {
      this.quietReckoningTimer.destroy();
      this.quietReckoningTimer = null;
    }
    if (this.quietReckoningContainer) {
      this.quietReckoningContainer.destroy();
      this.quietReckoningContainer = null;
    }
  }

  // ── 8. The Sunset ─────────────────────────────────────────────────────

  private handleTheSunset(payload: MilestoneTheSunsetPayload): void {
    this.destroySunset();

    const durationMs = (payload.durationSec ?? 45) * 1_000;
    const cam = this.scene.cameras.main;

    // Warm amber overlay — the star's last wash of colour across the world.
    this.sunsetOverlay = this.scene.add
      .rectangle(0, 0, cam.width, cam.height, 0xff8c42, 0.22)
      .setOrigin(0, 0)
      .setScrollFactor(0)
      .setDepth(SUNSET_DEPTH)
      .setAlpha(0);
    (this.scene as any).addToUI?.(this.sunsetOverlay);

    this.scene.tweens.add({
      targets: this.sunsetOverlay,
      alpha: 1,
      duration: SUNSET_FADE_IN_MS,
      ease: 'Sine.easeOut',
    });

    const fadeOutAt = Math.max(0, durationMs - SUNSET_FADE_OUT_MS);
    this.sunsetTimer = this.scene.time.delayedCall(fadeOutAt, () => {
      if (!this.sunsetOverlay) return;
      this.scene.tweens.add({
        targets: this.sunsetOverlay,
        alpha: 0,
        duration: SUNSET_FADE_OUT_MS,
        ease: 'Sine.easeIn',
        onComplete: () => this.destroySunset(),
      });
    });
  }

  private destroySunset(): void {
    if (this.sunsetTimer) {
      this.sunsetTimer.destroy();
      this.sunsetTimer = null;
    }
    if (this.sunsetOverlay) {
      this.sunsetOverlay.destroy();
      this.sunsetOverlay = null;
    }
  }
}
